import { AxisBottom } from "@/components/AxisBottom";
import { AxisLeft } from "@/components/AxisLeft";
import { formatDate, formatPrice } from "@/lib/formatters";
import { getStandardDeviation, regression } from "@/lib/stats";
import * as Portal from "@radix-ui/react-portal";
import * as d3 from "d3";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import useMeasure from "react-use-measure";
import { NormalizedDataPoint } from "../lib/PreparedDataset";
import RegressionModel from "@/types/RegressionModel";

const MARGIN = { top: 10, right: 10, bottom: 30, left: 90 };

type ScatterplotProps = {
  width: number;
  height: number;
  data: NormalizedDataPoint[];
  regressionType: RegressionModel | null;
  onPointClick: (point: NormalizedDataPoint) => void;
};

const pathAnimationProps = {
  transition: {
    type: "tween",
    ease: "easeOut",
  },
  initial: {
    pathLength: 0,
  },
  animate: {
    pathLength: 1,
  },
};

export const Scatterplot = ({
  width,
  height,
  data,
  regressionType,
  onPointClick,
}: ScatterplotProps) => {
  const boundsWidth = width - MARGIN.right - MARGIN.left;
  const boundsHeight = height - MARGIN.top - MARGIN.bottom;

  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);

  const yScale = d3
    .scaleLinear()
    .domain([d3.min(data.map((i) => i.price)) || 0, d3.max(data.map((i) => i.price)) || 0])
    .range([boundsHeight, 0]);

  const dataXMax = d3.max(data.map((i) => i.date.getTime()));
  const dataXMin = d3.min(data.map((i) => i.date.getTime()));

  const padding = (dataXMax && dataXMin && (dataXMax - dataXMin) * 0.02) || 0;
  const xScale = d3
    .scaleLinear()
    .domain([(dataXMin || 0) - padding, (dataXMax || 0) + padding])
    .range([0, boundsWidth]);

  const domain =
    dataXMin && dataXMax ? new Array(dataXMin - padding, dataXMax + padding) : undefined;

  const regressionData = useMemo(() => {
    if (!regressionType) return;
    return regression(
      regressionType,
      data.filter((i) => !i.outlier).map((i) => ({ x: i.date.getTime(), y: i.price })),
      domain,
    );
  }, [data, regressionType, domain]);
  const regressionPath =
    regressionData &&
    d3
      .line()
      .x((d) => xScale(d[0]))
      .y((d) => yScale(d[1]))(regressionData);

  (window as any).regressionPath = regressionPath;
  const standardDeviation = getStandardDeviation(data.map((i) => i.price));
  const standardDeviationPaths =
    standardDeviation && regressionData
      ? [
          d3
            .line()
            .x((d) => xScale(d[0]))
            .y((d) => yScale(d[1] + standardDeviation * 2))(regressionData),
          d3
            .line()
            .x((d) => xScale(d[0]))
            .y((d) => yScale(d[1] - standardDeviation * 2))(regressionData),
        ]
      : [];

  const [measureRef, measure] = useMeasure();

  return (
    <div className="relative" ref={measureRef}>
      <svg width={width} height={height}>
        <g
          width={boundsWidth}
          height={boundsHeight}
          transform={`translate(${[MARGIN.left, MARGIN.top].join(",")})`}
        >
          <AxisLeft yScale={yScale} pixelsPerTick={40} width={boundsWidth} />

          <g transform={`translate(0, ${boundsHeight})`}>
            <AxisBottom xScale={xScale} width={boundsWidth} height={boundsHeight} />
          </g>

          <motion.path
            className={"stroke-primary"}
            key={regressionType}
            {...pathAnimationProps}
            stroke="current"
            strokeWidth={2}
            strokeOpacity={0.6}
            fill="transparent"
            d={regressionPath || undefined}
            data-testid="regression-line"
          />

          {standardDeviationPaths?.map((path, i) => (
            <motion.path
              className={"stroke-foreground"}
              key={`standard-deviation-${i}`}
              stroke="current"
              strokeWidth={1}
              strokeOpacity={0.2}
              strokeDasharray="4 4"
              fill="transparent"
              d={path || undefined}
            />
          ))}

          {data.map((d, i) => {
            // const hovered = hoveredData === d.id;
            const className = d.outlier
              ? "cursor-pointer fill-muted-foreground/30"
              : "cursor-pointer";
            const fillOpacity = 0.6;

            return (
              <circle
                key={i}
                r={4}
                cx={xScale(d.date.getTime())}
                cy={yScale(d.price)}
                fill="current"
                className={className}
                fillOpacity={fillOpacity}
                onClick={() => onPointClick(d)}
                data-testid={`chart-point-${i}`}
                data-outlier={d.outlier}
                onMouseOver={() => {
                  setTooltipData({
                    xPos: xScale(d.date.getTime()),
                    yPos: yScale(d.price),
                    component: (
                      <div className="text-sm">
                        <div className="font-bold">{d.address}</div>
                        <div className="text-muted-foreground">{formatDate(d.date.getTime())}</div>
                        <div className="text-muted-foreground">{formatPrice(d.price)}</div>
                      </div>
                    ),
                  });
                }}
                onMouseLeave={() => {
                  setTooltipData(null);
                }}
              />
            );
          })}
        </g>
      </svg>

      <Portal.Root
        style={{
          position: "absolute",
          top: measure.top,
          left: measure.left,
          pointerEvents: "none",
          marginLeft: MARGIN.left,
          marginTop: MARGIN.top,
        }}
      >
        <Tooltip tooltipData={tooltipData}>{tooltipData?.component}</Tooltip>
      </Portal.Root>
    </div>
  );
};

type TooltipData = {
  xPos: number;
  yPos: number;
  component: React.ReactNode;
};

type TooltipProps = {
  tooltipData: TooltipData | null;
  children: React.ReactNode;
};
function Tooltip({ tooltipData, children }: TooltipProps) {
  const divRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const PADDING = 16;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const width = divRef.current ? divRef.current.offsetWidth : 0;
    setWidth(width);
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const height = divRef.current ? divRef.current.offsetHeight : 0;
    setHeight(height);
  });

  if (tooltipData === null) return null;

  let { xPos, yPos } = tooltipData;
  if (tooltipData.xPos + width + PADDING > window.innerWidth) {
    xPos = tooltipData.xPos - width - PADDING;
  } else {
    xPos = xPos + PADDING;
  }

  if (tooltipData.yPos + height + PADDING > window.innerHeight) {
    yPos = tooltipData.yPos - height - PADDING;
    yPos = yPos + PADDING;
  }

  return (
    <AnimatePresence>
      <motion.div
        key="tooltip"
        initial={{ top: yPos, left: xPos, opacity: 0 }}
        animate={{ top: yPos, left: xPos, opacity: 1 }}
        className="absolute"
        ref={divRef}
      >
        <div className="min-w-64 rounded border-2 border-foreground bg-background p-1 shadow-xl">
          {children}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
